import * as React from "react"

interface TabsProps {
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  className?: string
}

interface TabsListProps {
  children: React.ReactNode
  className?: string
}

interface TabsTriggerProps {
  value: string
  children: React.ReactNode
  className?: string
  onClick?: () => void
  'data-state'?: 'active' | 'inactive'
}

interface TabsContentProps {
  value: string
  children: React.ReactNode
  className?: string
  isActive?: boolean
}

const Tabs = ({ defaultValue, value, onValueChange, children, className }: TabsProps) => {
  const [activeTab, setActiveTab] = React.useState(value || defaultValue || '')
  
  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue)
    onValueChange?.(newValue)
  }
  
  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === TabsList) {
            return React.cloneElement(child as React.ReactElement<TabsListProps>, {
              children: React.Children.map(child.props.children, (trigger) => {
                if (React.isValidElement(trigger) && trigger.type === TabsTrigger) {
                  const triggerEl = trigger as React.ReactElement<TabsTriggerProps>;
                  const isActive = triggerEl.props.value === activeTab;
                  return React.cloneElement(triggerEl, {
                    'data-state': isActive ? 'active' : 'inactive',
                    onClick: () => handleTabChange(triggerEl.props.value)
                  } as Partial<TabsTriggerProps>)
                }
                return trigger
              })
            } as Partial<TabsListProps>)
          }
          if (child.type === TabsContent) {
            const childEl = child as React.ReactElement<TabsContentProps>;
            const isActive = childEl.props.value === activeTab;
            return React.cloneElement(childEl, {
              isActive
            } as Partial<TabsContentProps>)
          }
        }
        return child
      })}
    </div>
  )
}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={`inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-500 ${className}`}
      {...props}
    >
      {children}
    </div>
  )
)
TabsList.displayName = "TabsList"

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ children, className, 'data-state': dataState, value, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-tab={value}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
        dataState === 'active' 
          ? 'bg-white text-gray-900 shadow-sm' 
          : 'hover:text-gray-900'
      } ${className}`}
      {...props}
    >
      {children}
    </button>
  )
)
TabsTrigger.displayName = "TabsTrigger"

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ children, className, isActive, value, ...props }, ref) => {
    if (!isActive) return null
    
    return (
      <div
        ref={ref}
        data-tab={value}
        className={`mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
        {...props}
      >
        {children}
      </div>
    )
  }
)
TabsContent.displayName = "TabsContent"

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} 